import { Dispatch, SetStateAction, useCallback, useId } from 'react'
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  Button,
  makeStyles,
  shorthands,
  tokens,
  DialogOpenChangeEvent,
  DialogOpenChangeData,
} from '@fluentui/react-components'
import { Dismiss24Regular } from '@fluentui/react-icons'
import cssStyles from './FluentModal.module.css'

interface FluentModalProps {
  isModalOpen: boolean
  setIsModalOpen: Dispatch<SetStateAction<boolean>> | undefined
  isModeLess: boolean
  modalHeader: string
  modalContent: JSX.Element
  setModalInput?: Dispatch<SetStateAction<string | number | null>>
  setChangedValue?: Dispatch<SetStateAction<string>>
  styleOverrides?: {
    surface?: React.CSSProperties
  }
}

const useStyles = makeStyles({
  surface: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    ...shorthands.borderTop('4px', 'solid', tokens.colorBrandForeground1),
    maxWidth: '600px',
    minWidth: '400px',
    maxHeight: '80vh',
  },
  title: {
    ...shorthands.padding('12px', '12px', '14px', '24px'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase500,
    color: tokens.colorNeutralForeground1,
  },
  content: {
    ...shorthands.padding('0', '24px', '24px', '24px'),
    overflowY: 'auto',
    overflowX: 'hidden',
    '& p': { margin: '14px 0' },
    '& p:first-child': { marginTop: 0 },
    '& p:last-child': { marginBottom: 0 },
  },
  closeButton: {
    marginLeft: 'auto',
    marginTop: '4px',
    marginRight: '2px',
  },
})

export const FluentModal = ({
  isModalOpen,
  setIsModalOpen,
  isModeLess,
  modalHeader,
  modalContent,
  setModalInput,
  setChangedValue,
  styleOverrides,
}: FluentModalProps) => {
  const styles = useStyles()
  const titleId = useId()

  const closeModal = useCallback(() => {
    setModalInput && setModalInput(0)
    setIsModalOpen && setIsModalOpen(false)
    setChangedValue && setChangedValue('')
  }, [setChangedValue, setIsModalOpen, setModalInput])

  return (
    <Dialog
      open={isModalOpen}
      modalType={isModeLess ? 'non-modal' : 'modal'}
      onOpenChange={(
        _event: DialogOpenChangeEvent,
        data: DialogOpenChangeData
      ) => {
        if (!data.open) {
          closeModal()
        }
      }}
    >
      <DialogSurface className={styles.surface} style={styleOverrides?.surface}>
        <DialogTitle className={styles.title} id={titleId}>
          {modalHeader}
          <Button
            data-testid="CloseModalBtn"
            appearance="subtle"
            icon={<Dismiss24Regular />}
            onClick={closeModal}
            disabled={setIsModalOpen === undefined}
            className={styles.closeButton}
          />
        </DialogTitle>
        <DialogBody>
          <DialogContent
            className={`${styles.content} ${cssStyles.modalContent}`}
          >
            {modalContent}
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
